import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import flagService from "./flagService";
import entryService from "../entry/entryService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";
import { setEntryValid, setEntryInvalid } from "../entry/entrySlice";

const initialState = {
  flags: [],
  flag: null,
  flagsWithEntries: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

export const getFlags = createAsyncThunk(
  "flag/getFlags",
  async (_, thunkAPI) => {
    try {
      const response = await flagService.getFlags();
      // thunkAPI.dispatch(setAlertSuccess("Flags fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch flags";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getFlagsWithEntries = createAsyncThunk(
  "flag/getFlagsWithEntries",
  async (_, thunkAPI) => {
    try {
      const response = await flagService.getFlags();
      const flags = response.data.flags || [];

      // Fetch entries with lookup details from the server
      const allEntries = await entryService.getEntriesWithDetails();
      const entries = allEntries.data.entries || allEntries.entries || [];

      // Normalize date format for comparison (YYYY-MM-DD)
      const normalizeDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      // Create a map of entries for easier lookup by ID and by event_name/date
      const entriesById = new Map();
      const entriesByEventDate = new Map();

      entries.forEach((entry) => {
        // Map by entry ID for direct lookup
        entriesById.set(entry.id, entry);

        // Also map by event/date for fallback matching
        const normalizedDate = normalizeDate(entry.event_date);
        const key = `${(entry.event || "").toLowerCase()}-${normalizedDate}`;
        if (!entriesByEventDate.has(key)) {
          entriesByEventDate.set(key, []);
        }
        entriesByEventDate.get(key).push(entry);
      });

      // Add matched entries to flags
      const flagsWithEntries = flags.map((flag) => {
        let matchedEntries = [];

        // First priority: If flag has entry_id, use it directly
        if (flag.entry_id) {
          const entry = entriesById.get(flag.entry_id);
          if (entry) {
            matchedEntries = [entry];
          }
        } else {
          // Fallback: Match by event_name and date, then filter by player_name
          const normalizedDate = normalizeDate(flag.event_date);
          const key = `${(flag.event_name || "").toLowerCase()}-${normalizedDate}`;
          const entriesByEventDateList = entriesByEventDate.get(key) || [];

          // Filter by player_name if available (case-insensitive)
          const playerFilteredEntries = entriesByEventDateList.filter(
            (entry) => {
              if (!entry.player_name && !entry.player) {
                return false; // Skip entries without player info
              }
              // Try to match by player_name if available
              if (entry.player_name) {
                return (
                  entry.player_name.toLowerCase() ===
                  flag.player_name.toLowerCase()
                );
              }
              // Otherwise return the entry (might be matched later)
              return true;
            },
          );

          matchedEntries =
            playerFilteredEntries.length > 0 ? playerFilteredEntries : [];
        }

        return {
          ...flag,
          entries: matchedEntries,
        };
      });

      return { data: { flags: flagsWithEntries } };
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message ||
        error.message ||
        "Failed to fetch flags with entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getFlagById = createAsyncThunk(
  "flag/getFlagById",
  async (flagId, thunkAPI) => {
    try {
      const response = await flagService.getFlagById(flagId);
      // thunkAPI.dispatch(setAlertSuccess("Flag fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const createFlag = createAsyncThunk(
  "flag/createFlag",
  async (flagData, thunkAPI) => {
    try {
      const response = await flagService.createFlag(flagData);
      thunkAPI.dispatch(setAlertSuccess("Flag created successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to create flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const updateFlag = createAsyncThunk(
  "flag/updateFlag",
  async (flagData, thunkAPI) => {
    try {
      const response = await flagService.updateFlag(flagData);
      thunkAPI.dispatch(setAlertSuccess("Flag updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to update flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const deleteFlag = createAsyncThunk(
  "flag/deleteFlag",
  async (flagId, thunkAPI) => {
    try {
      const response = await flagService.deleteFlag(flagId);
      thunkAPI.dispatch(setAlertSuccess("Flag deleted successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to delete flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

const flagSlice = createSlice({
  name: "flag",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      state.flag = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFlags.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flags = action.payload.data.flags || [];
      })
      .addCase(getFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getFlagsWithEntries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlagsWithEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flagsWithEntries = action.payload.data.flags || [];
      })
      .addCase(getFlagsWithEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getFlagById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlagById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flag = action.payload.data.flag || null;
      })
      .addCase(getFlagById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(createFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flags.push(action.payload.data.flag);
      })
      .addCase(createFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const index = state.flags.findIndex(
          (flag) => flag.id === action.payload.data.flag.id,
        );
        if (index !== -1) {
          state.flags[index] = action.payload.data.flag;
        }
      })
      .addCase(updateFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deleteFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flags = state.flags.filter(
          (flag) => flag.id !== action.payload.data.flagId,
        );
      })
      .addCase(deleteFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(setEntryValid.fulfilled, (state, action) => {
        const updatedEntry = action.payload.data.entry;
        state.flagsWithEntries = state.flagsWithEntries.map((flag) => ({
          ...flag,
          entries: flag.entries.map((entry) =>
            entry.id === updatedEntry.id ? updatedEntry : entry,
          ),
        }));
      })
      .addCase(setEntryInvalid.fulfilled, (state, action) => {
        const updatedEntry = action.payload.data.entry;
        state.flagsWithEntries = state.flagsWithEntries.map((flag) => ({
          ...flag,
          entries: flag.entries.map((entry) =>
            entry.id === updatedEntry.id ? updatedEntry : entry,
          ),
        }));
      });
  },
});

export const { reset } = flagSlice.actions;
export default flagSlice.reducer;
